import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  ListSubheader,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import MonetizationOnRoundedIcon from "@mui/icons-material/MonetizationOnRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import SearchIcon from "@mui/icons-material/Search";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import { toast } from "react-toastify";
import ExecutiveOverview from "./components/ExecutiveOverview";
import PaymentPlatformAddCompanyFlow from "./components/PaymentPlatformAddCompanyFlow";
import PaymentPlatformExecutiveTable from "./components/PaymentPlatformExecutiveTable";
import {
  exportPaymentPlatformEntities,
  getPaymentPlatformFullUnderwritingJob,
  getPaymentPlatformOverview,
  searchPaymentPlatformEntities,
  startPaymentPlatformFullUnderwriting,
} from "./api/companyApi";
import { buildPaymentPlatformCopilotContext } from "./utils/paymentPlatformCopilotContext";

const fmt = (value) => Number(value || 0).toLocaleString();
const fmtMoney = (value) => value == null
  ? "—"
  : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 }).format(Number(value) || 0);
const fmtAverage = (value, suffix = "") => value == null ? "—" : `${Number(value).toFixed(1)}${suffix}`;
const fmtFullMoney = (value) => value == null ? "—" : `$${Number(value || 0).toLocaleString()}`;
const fmtPercent = (value) => value == null ? "—" : `${(Number(value) * 100).toFixed(1)}%`;

export function PaymentPlatformFilteredSummary({ summary, loading }) {
  const values = summary || {};
  const cards = [
    ["Filtered merchants", fmt(values.entityCount), "Entire filtered portfolio"],
    ["Applications", fmt(values.applicationCount), "Across all matching merchants"],
    ["Approvals", fmt(values.approvedCount), `${fmt(values.approvalRate)}% of filtered merchants`],
    ["Average model score", fmtAverage(values.averageScore), `${fmtAverage(values.averageRiskScore)} average risk score`],
    ["Requested amount", fmtFullMoney(values.totalRequestedAmount), `${fmtAverage(values.averageCoverage, "%")} average evidence`],
    ["Recommended amount", fmtFullMoney(values.totalRecommendedAmount), `${fmt(values.highRiskCount)} high-risk merchants`],
  ];
  return (
    <Box
      data-mca-filtered-portfolio-summary
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)", xl: "repeat(6, 1fr)" },
        gap: 1.25,
        mt: 2,
      }}
    >
      {cards.map(([label, value, detail]) => (
        <Paper key={label} variant="outlined" sx={{ p: 1.5, borderRadius: "12px", borderColor: "#e4e9f2", minWidth: 0 }}>
          <Typography sx={{ color: "#6c7890", fontSize: 10.5, fontWeight: 800, textTransform: "uppercase" }}>{label}</Typography>
          <Typography sx={{ color: "#17233f", fontSize: 22, fontWeight: 800, lineHeight: 1.25, mt: .25 }}>{loading ? "…" : value}</Typography>
          <Typography sx={{ color: "#6c7890", fontSize: 11.5, mt: .25 }}>{loading ? "\u00a0" : detail}</Typography>
        </Paper>
      ))}
    </Box>
  );
}

function LiberisRiskScorePivot({ job }) {
  const rows = Array.isArray(job?.riskScorePivot) ? job.riskScorePivot : [];
  const totals = job?.applicationPortfolioTotals;
  if (!rows.length || !totals) return null;
  const displayRows = [
    ...rows,
    {
      riskScore: "Total",
      recommendedApplications: totals.recommendedApplications,
      firstAdvanceAmount: totals.firstAdvanceAmount,
      averageAdvance: totals.averageAdvance,
      factorRate: totals.weightedFactorRate,
      holdbackPct: totals.weightedHoldbackPct,
      baseDefaultRate: totals.baseDefaultRate,
      stressDefaultRate: totals.stressDefaultRate,
      projectedOriginations12m: totals.projectedOriginations12m,
      baseNetRevenue12m: totals.baseNetRevenue12m,
    },
  ];
  const columns = [
    ["Risk score", (row) => row.riskScore],
    ["Recommended applications", (row) => fmt(row.recommendedApplications)],
    ["First advances", (row) => fmtFullMoney(row.firstAdvanceAmount)],
    ["Average advance", (row) => fmtFullMoney(Math.round(Number(row.averageAdvance || 0)))],
    ["Factor", (row) => Number(row.factorRate || 0).toFixed(2)],
    ["Holdback", (row) => fmtPercent(row.holdbackPct)],
    ["Base default", (row) => fmtPercent(row.baseDefaultRate)],
    ["Stress default", (row) => fmtPercent(row.stressDefaultRate)],
    ["12m originations", (row) => fmtFullMoney(Math.round(Number(row.projectedOriginations12m || 0)))],
    ["12m net revenue", (row) => fmtFullMoney(Math.round(Number(row.baseNetRevenue12m || 0)))],
  ];
  return (
    <Paper
      data-liberis-risk-score-pivot
      variant="outlined"
      sx={{ mt: 3, borderRadius: "18px", overflow: "hidden", borderColor: "#e4e9f2", boxShadow: "0 12px 30px rgba(35,54,91,.06)" }}
    >
      <Box sx={{ px: { xs: 2, md: 2.75 }, py: 2.5 }}>
        <Typography sx={{ color: "#17233f", fontWeight: 800, fontSize: 20, letterSpacing: "-.015em" }}>
          Recommended portfolio by risk score
        </Typography>
        <Typography sx={{ color: "#6c7890", fontSize: 13, mt: .4 }}>
          Application-level planning view. The headline above uses each merchant&apos;s latest offer; this table includes all recommended applications.
        </Typography>
        <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", mt: 2 }}>
          {[
            ["Recommended applications", fmt(totals.recommendedApplications)],
            ["First-advance capacity", fmtFullMoney(totals.firstAdvanceAmount)],
            ["12-month projected originations", fmtFullMoney(Math.round(Number(totals.projectedOriginations12m || 0)))],
            ["Base expected loss", fmtFullMoney(Math.round(Number(totals.baseExpectedLoss12m || 0)))],
            ["Base net factor revenue", fmtFullMoney(Math.round(Number(totals.baseNetRevenue12m || 0)))],
          ].map(([label, value]) => (
            <Box key={label} sx={{ minWidth: 165, flex: "1 1 165px", p: 1.25, bgcolor: "#f7f9fc", borderRadius: "10px" }}>
              <Typography sx={{ color: "#6c7890", fontSize: 10.5, fontWeight: 800, textTransform: "uppercase" }}>{label}</Typography>
              <Typography sx={{ color: "#17233f", fontSize: 19, fontWeight: 800, mt: .25 }}>{value}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={{ overflowX: "auto", borderTop: "1px solid #edf0f5" }}>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 1120 }}>
          <Box component="thead" sx={{ bgcolor: "#f7f9fc" }}>
            <Box component="tr">
              {columns.map(([label]) => (
                <Box component="th" key={label} sx={{ px: 1.5, py: 1.25, color: "#64748b", fontSize: 10.5, fontWeight: 800, textTransform: "uppercase", textAlign: label === "Risk score" ? "left" : "right", whiteSpace: "nowrap" }}>
                  {label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {displayRows.map((row) => {
              const isTotal = row.riskScore === "Total";
              return (
                <Box component="tr" key={row.riskScore} sx={{ bgcolor: isTotal ? "#eef4ff" : "#fff" }}>
                  {columns.map(([label, render]) => (
                    <Box component="td" key={label} sx={{ px: 1.5, py: 1.2, borderTop: "1px solid #edf0f5", color: "#17233f", fontSize: 12.5, fontWeight: isTotal ? 800 : 600, textAlign: label === "Risk score" ? "left" : "right", whiteSpace: "nowrap" }}>
                      {render(row)}
                    </Box>
                  ))}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
      <Typography sx={{ px: { xs: 2, md: 2.75 }, py: 1.5, color: "#6c7890", fontSize: 11.5, borderTop: "1px solid #edf0f5" }}>
        Base case assumes 50% renewal take-up, no more than two advances per merchant, and 100% loss given default. Default rates are inactivity-linked planning estimates, not observed all-cause defaults.
      </Typography>
    </Paper>
  );
}

function PaymentPlatformPortfolioTable({ tenantId, refreshKey, onAddCompany }) {
  const navigate = useNavigate();
  const [result, setResult] = useState({ items: [], nextCursor: null, total: null });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [decision, setDecision] = useState("");
  const [risk, setRisk] = useState("");
  const [coverage, setCoverage] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);
  const [cursors, setCursors] = useState([null]);
  const [error, setError] = useState("");
  const [exportAnchor, setExportAnchor] = useState(null);
  const [exporting, setExporting] = useState("");
  const [exportError, setExportError] = useState("");
  const [exportSort, setExportSort] = useState({ key: "score", direction: "desc" });
  const [refreshJobs, setRefreshJobs] = useState({});
  const [reportRefreshKey, setReportRefreshKey] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(0);
    setCursors([null]);
  }, [debouncedSearch, decision, risk, coverage, pageSize]);

  useEffect(() => {
    if (!tenantId) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    searchPaymentPlatformEntities(tenantId, {
      query: debouncedSearch || undefined,
      decision: decision || undefined,
      risk: risk || undefined,
      minCoverage: coverage || undefined,
      pageSize,
      cursor: cursors[page] || undefined,
      includeSummary: page === 0,
    })
      .then((data) => {
        if (active) {
          setResult((current) => ({
            ...(data || { items: [], nextCursor: null, total: null }),
            total: data?.total ?? current.total ?? null,
            summary: data?.summary || current.summary || null,
          }));
        }
      })
      .catch((requestError) => {
        if (active) setError(requestError?.response?.data?.message || "Could not load the portfolio table.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [tenantId, debouncedSearch, decision, risk, coverage, pageSize, page, cursors, refreshKey, reportRefreshKey]);

  const refreshJobEntries = useMemo(
    () => Object.entries(refreshJobs).filter(([, job]) => job?.jobId),
    [refreshJobs],
  );
  const refreshJobKey = refreshJobEntries
    .map(([entityId, job]) => `${entityId}:${job.jobId}`)
    .sort()
    .join("|");

  useEffect(() => {
    if (!tenantId || !refreshJobEntries.length) return undefined;
    let active = true;
    const poll = async () => {
      const updates = await Promise.all(refreshJobEntries.map(async ([entityId, job]) => {
        try {
          return [entityId, await getPaymentPlatformFullUnderwritingJob(tenantId, job.jobId)];
        } catch (requestError) {
          return [entityId, null];
        }
      }));
      if (!active) return;
      for (const [entityId, job] of updates) {
        if (!job) continue;
        if (job.status === "done") {
          setRefreshJobs((current) => {
            const next = { ...current };
            delete next[entityId];
            return next;
          });
          setReportRefreshKey((current) => current + 1);
          toast.success("Underwriting report refreshed");
        } else if (job.status === "error") {
          setRefreshJobs((current) => {
            const next = { ...current };
            delete next[entityId];
            return next;
          });
          toast.error(job.message || "The underwriting report refresh failed");
        } else {
          setRefreshJobs((current) => ({ ...current, [entityId]: job }));
        }
      }
    };
    poll();
    const timer = window.setInterval(poll, 4000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  // Job status updates must not restart the polling interval; job IDs define the active work.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, refreshJobKey]);

  const refreshCompanyReport = async (entityId) => {
    if (!entityId || refreshJobs[entityId]) return;
    setRefreshJobs((current) => ({ ...current, [entityId]: { status: "starting" } }));
    try {
      const job = await startPaymentPlatformFullUnderwriting(tenantId, entityId);
      setRefreshJobs((current) => ({ ...current, [entityId]: job }));
      toast.info("Refreshing evidence and running the underwriting prompt");
    } catch (requestError) {
      setRefreshJobs((current) => {
        const next = { ...current };
        delete next[entityId];
        return next;
      });
      toast.error(requestError?.response?.data?.message || "Could not refresh the underwriting report");
    }
  };

  const nextPage = () => {
    if (!result.nextCursor) return;
    setCursors((current) => {
      const next = current.slice(0, page + 1);
      next[page + 1] = result.nextCursor;
      return next;
    });
    setPage((current) => current + 1);
  };

  const openCompany = (entityId) =>
    navigate(`/tenant-dashboard/${tenantId}/assessments/company/${entityId}`);
  const exportPortfolio = useCallback(async ({ format, sortKey, sortDirection }) => {
    const blob = await exportPaymentPlatformEntities(tenantId, {
      query: debouncedSearch || undefined,
      decision: decision || undefined,
      risk: risk || undefined,
      minCoverage: coverage || undefined,
      format,
      sortKey,
      sortDirection,
    });
    const extension = format === "xlsx" ? "xlsx" : "csv";
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `crediarc-${tenantId}-mca-assessments-${new Date().toISOString().slice(0, 10)}.${extension}`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }, [tenantId, debouncedSearch, decision, risk, coverage]);
  const runPortfolioExport = async (format) => {
    setExportAnchor(null);
    setExportError("");
    setExporting(format);
    try {
      await exportPortfolio({ format, sortKey: exportSort.key, sortDirection: exportSort.direction });
    } catch (requestError) {
      setExportError(requestError?.response?.data?.message || requestError?.message || "Could not export the filtered portfolio.");
    } finally {
      setExporting("");
    }
  };

  return (
    <Paper
      data-payment-platform-portfolio-table
      variant="outlined"
      sx={{ mt: 3, mb: 3, borderRadius: "18px", overflow: "hidden", borderColor: "#e4e9f2", boxShadow: "0 12px 30px rgba(35,54,91,.06)" }}
    >
      <Box sx={{ px: { xs: 2, md: 2.75 }, pt: 2.5, pb: 2, bgcolor: "#fff" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography sx={{ color: "#17233f", fontWeight: 700, fontSize: 20, letterSpacing: "-.015em" }}>Portfolio assessments</Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Button
              data-mca-portfolio-export
              startIcon={<FileDownloadOutlinedIcon />}
              variant="outlined"
              size="small"
              disabled={loading || !Number(result.total || result.items?.length) || Boolean(exporting)}
              onClick={(event) => setExportAnchor(event.currentTarget)}
              sx={{ borderRadius: "9px", textTransform: "none", fontWeight: 800 }}
            >
              {exporting ? "Preparing export…" : "Export"}
            </Button>
            <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
              <ListSubheader sx={{ lineHeight: "32px", fontSize: 11, fontWeight: 800, textTransform: "uppercase", color: "#64748b" }}>
                Complete filtered portfolio · {Number(result.total || result.items?.length || 0).toLocaleString()} rows
              </ListSubheader>
              <MenuItem onClick={() => runPortfolioExport("xlsx")}>Excel workbook — all filtered rows</MenuItem>
              <MenuItem onClick={() => runPortfolioExport("csv")}>CSV data — all filtered rows</MenuItem>
            </Menu>
            <Button startIcon={<AddBusinessIcon />} variant="contained" size="small" onClick={onAddCompany} sx={{ borderRadius: "9px", textTransform: "none", fontWeight: 700 }}>+ Add company</Button>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ color: "#6c7890", mt: .25, mb: 2 }}>
          Every Liberis merchant assessment, with full search, filtering and company reports.
        </Typography>
        <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, merchant ID, EIN, email, website or application…"
            sx={{ flex: "1 1 390px" }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
          <TextField select size="small" label="Decision" value={decision} onChange={(event) => setDecision(event.target.value)} sx={{ minWidth: 145 }}>
            <MenuItem value="">All decisions</MenuItem>
            <MenuItem value="approve">Approve</MenuItem>
            <MenuItem value="review">Review</MenuItem>
            <MenuItem value="decline">Decline</MenuItem>
            <MenuItem value="insufficient_data">Insufficient data</MenuItem>
          </TextField>
          <TextField select size="small" label="Risk" value={risk} onChange={(event) => setRisk(event.target.value)} sx={{ minWidth: 125 }}>
            <MenuItem value="">All risk</MenuItem>
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
          </TextField>
          <TextField select size="small" label="Evidence" value={coverage} onChange={(event) => setCoverage(event.target.value)} sx={{ minWidth: 145 }}>
            <MenuItem value="">Any coverage</MenuItem>
            <MenuItem value="25">25%+</MenuItem>
            <MenuItem value="50">50%+</MenuItem>
            <MenuItem value="75">75%+</MenuItem>
          </TextField>
        </Box>
        <PaymentPlatformFilteredSummary summary={result.summary} loading={loading && page === 0} />
        {exportError && <Alert severity="error" onClose={() => setExportError("")} sx={{ mt: 1.5 }}>{exportError}</Alert>}
      </Box>
      {error ? (
        <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
      ) : loading ? (
        <Box sx={{ py: 7, textAlign: "center" }}><CircularProgress size={30} /></Box>
      ) : (
        <PaymentPlatformExecutiveTable
          rows={result.items || []}
          onOpenCompany={openCompany}
          onRefreshCompany={refreshCompanyReport}
          refreshingEntityIds={Object.keys(refreshJobs)}
          onSortChange={setExportSort}
          filteredTotal={result.total}
          hideExport
          viewport="portfolio-assessments"
        />
      )}
      <Box sx={{ px: { xs: 2, md: 2.75 }, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", borderTop: "1px solid #edf0f5" }}>
        <Typography sx={{ color: "#6c7890", fontSize: 13 }}>
          Page {page + 1}{result.total != null ? ` · ${fmt(result.total)} companies` : ""}{result.searchLimited ? " · first 1,000 search matches" : ""}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField select size="small" label="Rows" value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))} sx={{ width: 90 }}>
            <MenuItem value={25}>25</MenuItem><MenuItem value={50}>50</MenuItem><MenuItem value={100}>100</MenuItem>
          </TextField>
          <Button variant="outlined" disabled={page === 0 || loading} onClick={() => setPage((current) => Math.max(0, current - 1))}>Previous</Button>
          <Button variant="contained" disabled={!result.nextCursor || loading} onClick={nextPage}>Next</Button>
        </Box>
      </Box>
    </Paper>
  );
}

export default function PaymentPlatformDashboardPage() {
  const navigate = useNavigate();
  const { setPortfolioCopilotContext } = useOutletContext() || {};
  const { tenantIdp } = useParams();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [portfolioRefreshKey, setPortfolioRefreshKey] = useState(0);

  const load = useCallback(async () => {
    if (!tenantIdp) return;
    setLoading(true);
    setError("");
    try {
      const overviewData = await getPaymentPlatformOverview(tenantIdp);
      setOverview(overviewData);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Could not load the payment-platform dashboard.");
    } finally {
      setLoading(false);
    }
  }, [tenantIdp]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let lastRefreshAt = 0;
    const refreshWhenVisible = () => {
      if (document.visibilityState === "hidden") return;
      const timestamp = Date.now();
      if (timestamp - lastRefreshAt < 2000) return;
      lastRefreshAt = timestamp;
      load();
      setPortfolioRefreshKey((current) => current + 1);
    };
    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [load]);
  useEffect(() => {
    if (!setPortfolioCopilotContext || !tenantIdp) return undefined;
    const data = buildPaymentPlatformCopilotContext(overview);
    setPortfolioCopilotContext({ tenantId: tenantIdp, data });
    return () => setPortfolioCopilotContext((current) => (
      current?.tenantId === tenantIdp && current?.data?.workspace === "MCA / Loan Assessments"
        ? null
        : current
    ));
  }, [overview, setPortfolioCopilotContext, tenantIdp]);

  const job = overview?.latestImport;
  const decisions = job?.decisionDistribution || {};
  const risks = job?.riskDistribution || {};
  const aggregates = job?.aggregates || {};
  const matched = Number(job?.matchedEntities || 0);
  const assessed = Number(job?.assessedEntities || 0);
  const decisionTotal = Object.values(decisions).reduce((sum, value) => sum + Number(value || 0), 0);
  const approvalRate = decisionTotal ? (Number(decisions.approve || 0) / decisionTotal) * 100 : 0;
  const riskTotal = Number(risks.low || 0) + Number(risks.medium || 0) + Number(risks.high || 0);
  const fallbackRiskScore = riskTotal
    ? (Number(risks.low || 0) * 2.5 + Number(risks.medium || 0) * 6 + Number(risks.high || 0) * 8.5) / riskTotal
    : 0;
  const portfolioScore = aggregates.averageRiskScore == null
    ? fallbackRiskScore
    : Number(aggregates.averageRiskScore) / 10;
  const scrollToPortfolio = useCallback(() => {
    document.querySelector("[data-payment-platform-portfolio-table]")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);
  const metricCards = useMemo(() => [
    {
      eyebrow: "Assessed merchants",
      value: fmt(assessed || matched),
      detail: `${fmt(job?.unmatchedRows)} merchants require additional identity review.`,
      accent: "#3168e0",
      icon: <BusinessRoundedIcon />,
      action: "View portfolio",
      onClick: scrollToPortfolio,
    },
    {
      eyebrow: tenantIdp === "liberis" ? "Approved merchants (latest)" : "Approvals",
      value: fmt(decisions.approve),
      detail: tenantIdp === "liberis"
        ? `${fmtAverage(approvalRate, "%")} of assessed merchants · latest application per MerchantID.`
        : `${fmtAverage(approvalRate, "%")} of assessed merchants · model v${overview?.model?.version || 1}.`,
      accent: "#35a477",
      icon: <CheckCircleRoundedIcon />,
      action: "View approved merchants",
      onClick: scrollToPortfolio,
    },
    {
      eyebrow: "Approved amount",
      value: fmtMoney(aggregates.approvedRecommendedAmount),
      detail: "Total recommended advance for approved merchants.",
      accent: "#22846a",
      icon: <MonetizationOnRoundedIcon />,
      action: "Review approved capacity",
      onClick: scrollToPortfolio,
    },
    {
      eyebrow: "Average PD score",
      value: fmtAverage(aggregates.averagePdScore),
      detail: aggregates.pdScoredEntities
        ? `Based on ${fmt(aggregates.pdScoredEntities)} merchants with an imported PD output.`
        : "Available when the import includes a mapped PD score output.",
      accent: "#7456c7",
      icon: <QueryStatsRoundedIcon />,
    },
    {
      eyebrow: "Average model score",
      value: fmtAverage(aggregates.averageScore),
      detail: `${fmtAverage(aggregates.averageCoverage, "%")} average evidence coverage.`,
      accent: "#3168e0",
      icon: <QueryStatsRoundedIcon />,
    },
    {
      eyebrow: "Decision queue",
      value: fmt(decisions.review),
      detail: `${fmt(decisions.decline)} declined · ${fmt(risks.high)} high-risk merchants.`,
      accent: "#d79a27",
      icon: <ScheduleRoundedIcon />,
      action: "Review decisions",
      onClick: scrollToPortfolio,
    },
  ], [assessed, matched, job?.unmatchedRows, decisions.review, decisions.decline, decisions.approve, risks.high, approvalRate, aggregates.approvedRecommendedAmount, aggregates.averagePdScore, aggregates.pdScoredEntities, aggregates.averageScore, aggregates.averageCoverage, overview?.model?.version, scrollToPortfolio, tenantIdp]);

  if (loading) {
    return <Box sx={{ minHeight: 420, display: "grid", placeItems: "center" }}><CircularProgress aria-label="Loading payment-platform dashboard" /></Box>;
  }
  if (error || !overview) {
    return (
      <Alert
        severity="error"
        action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
      >
        {error || "Dashboard data is unavailable."}
      </Alert>
    );
  }

  return (
    <Box data-payment-platform-dashboard>
      <ExecutiveOverview
        isProspects={false}
        portfolioScore={portfolioScore}
        prospectsCount={0}
        approvedLimit={0}
        existingCustomers={matched}
        customersAtRisk={Number(risks.high || 0)}
        moneyAtRisk={0}
        onReviewRisk={scrollToPortfolio}
        onLoadAging={load}
        scoreLabel="Average portfolio risk score"
        intelligenceLabel="Live MCA / loan intelligence"
        metricCards={metricCards}
      />
      {tenantIdp === "liberis" && <LiberisRiskScorePivot job={job} />}
      <PaymentPlatformPortfolioTable tenantId={tenantIdp} refreshKey={portfolioRefreshKey} onAddCompany={() => setAddOpen(true)} />
      <PaymentPlatformAddCompanyFlow
        tenantId={tenantIdp}
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => {
          setPortfolioRefreshKey((current) => current + 1);
          load();
        }}
        onOpenReport={(entityId) => navigate(`/tenant-dashboard/${tenantIdp}/assessments/company/${entityId}`)}
      />
    </Box>
  );
}
